import { money, multiplyByRatio, type Money } from "@stb/core/money"
import {
  INCOME_TAX_YEARS,
  soleTraderTax,
  takeHomePay,
  type Residency,
  type SoleTraderResult,
  type TakeHomeResult,
} from "@stb/jurisdiction-au/income-tax"

import type { Locale } from "@/core/i18n/locale"

import { SOLE_MODEL_COPY_ZH } from "../../content/zh/sole-trader-calculator-copy"
import {
  DEFAULT_FY,
  MAX_AMOUNT,
  normalizeAmountInput,
  parseAmount,
  parseDays,
  parseHours,
} from "./pay-calculator-model"

/**
 * Form state of the sole trader calculator (T916). It sits on the same engine as the pay calculator
 * (`soleTraderTax` and `takeHomePay` in @stb/jurisdiction-au); this file only validates, converts a rate to a
 * year and picks what the page shows. Text fields stay strings so typing is never fought.
 */
export type SolePeriod = "year" | "month" | "week" | "day" | "hour"
export type SoleShow = "month" | "quarter"

export interface SoleForm {
  /** Income before GST, per `period`. */
  income: string
  period: SolePeriod
  hoursPerWeek: string
  daysPerWeek: string
  /** Weeks worked in the year; the weekly, daily and hourly rates are multiplied by it. */
  weeksPerYear: string
  /** Deductible business expenses, for the whole year. */
  expenses: string
  /** Wages from a job, for the whole year. */
  wages: string
  fy: string
  residency: Residency
  help: boolean
  medicare: boolean
  show: SoleShow
}

export const SOLE_PERIODS: readonly SolePeriod[] = ["year", "month", "week", "day", "hour"]
export const SOLE_SHOWN: readonly SoleShow[] = ["month", "quarter"]
const RESIDENCY_PARAM: Record<Residency, string> = {
  resident: "resident",
  foreign: "foreign",
  "working-holiday": "whm",
}

export const DEFAULT_SOLE_FORM: SoleForm = {
  income: "100000",
  period: "year",
  hoursPerWeek: "38",
  daysPerWeek: "5",
  weeksPerYear: "52",
  expenses: "20000",
  wages: "0",
  fy: DEFAULT_FY,
  residency: "resident",
  help: false,
  medicare: true,
  show: "month",
}

/** Whole weeks, 1 to 52. */
export function parseWeeks(text: string): number | undefined {
  if (!/^\d{1,2}$/.test(text.trim())) return undefined
  const n = Number(text)
  return n >= 1 && n <= 52 ? n : undefined
}

const oneOf = <T extends string>(allowed: readonly T[], value: string | null, fallback: T): T =>
  allowed.find((a) => a === value) ?? fallback
const amountParam = (value: string | null, fallback: string) =>
  value !== null && parseAmount(value) ? value : fallback
const hoursParam = (value: string | null, fallback: string) =>
  value !== null && parseHours(value) !== undefined ? value : fallback
const daysParam = (value: string | null, fallback: string) =>
  value !== null && parseDays(value) !== undefined ? value : fallback
const weeksParam = (value: string | null, fallback: string) =>
  value !== null && parseWeeks(value) !== undefined ? value : fallback

/** Whitelist parse: unknown, out of range or missing values fall back to the default; never throws. */
export function parseSoleForm(params: URLSearchParams, d: SoleForm = DEFAULT_SOLE_FORM): SoleForm {
  const res = params.get("res")
  return {
    income: normalizeAmountInput(amountParam(params.get("amount"), d.income)),
    period: oneOf(SOLE_PERIODS, params.get("period"), d.period),
    hoursPerWeek: hoursParam(params.get("hpw"), d.hoursPerWeek),
    daysPerWeek: daysParam(params.get("dpw"), d.daysPerWeek),
    weeksPerYear: weeksParam(params.get("weeks"), d.weeksPerYear),
    expenses: normalizeAmountInput(amountParam(params.get("exp"), d.expenses)),
    wages: normalizeAmountInput(amountParam(params.get("wages"), d.wages)),
    fy: oneOf(
      INCOME_TAX_YEARS.map((y) => y.financialYear),
      params.get("fy"),
      d.fy,
    ),
    residency:
      (Object.keys(RESIDENCY_PARAM) as Residency[]).find((r) => RESIDENCY_PARAM[r] === res) ??
      d.residency,
    help: params.get("help") === null ? d.help : params.get("help") === "1",
    medicare: params.get("med") === null ? d.medicare : params.get("med") === "1",
    show: oneOf(SOLE_SHOWN, params.get("show"), d.show),
  }
}

/** Only values that differ from the default are written, so the shared link stays short. */
export function formToSoleParams(form: SoleForm, d: SoleForm = DEFAULT_SOLE_FORM): URLSearchParams {
  const p = new URLSearchParams()
  if (form.income !== d.income && parseAmount(form.income)) p.set("amount", form.income.trim())
  if (form.period !== d.period) p.set("period", form.period)
  if (form.hoursPerWeek !== d.hoursPerWeek && parseHours(form.hoursPerWeek) !== undefined)
    p.set("hpw", form.hoursPerWeek.trim())
  if (form.daysPerWeek !== d.daysPerWeek && parseDays(form.daysPerWeek) !== undefined)
    p.set("dpw", form.daysPerWeek.trim())
  if (form.weeksPerYear !== d.weeksPerYear && parseWeeks(form.weeksPerYear) !== undefined)
    p.set("weeks", form.weeksPerYear.trim())
  if (form.expenses !== d.expenses && parseAmount(form.expenses)) p.set("exp", form.expenses.trim())
  if (form.wages !== d.wages && parseAmount(form.wages)) p.set("wages", form.wages.trim())
  if (form.fy !== d.fy) p.set("fy", form.fy)
  if (form.residency !== d.residency) p.set("res", RESIDENCY_PARAM[form.residency])
  if (form.help !== d.help) p.set("help", form.help ? "1" : "0")
  if (form.medicare !== d.medicare) p.set("med", form.medicare ? "1" : "0")
  if (form.show !== d.show) p.set("show", form.show)
  return p
}

export type SoleOutcome =
  | {
      kind: "ok"
      result: SoleTraderResult
      /** Business income for the year, before GST. */
      annualIncome: Money
      expenses: Money
      wages: Money
      /** The same pay as an employee's salary (no super in it), with the same wages as another job. */
      employee: TakeHomeResult
    }
  | {
      kind: "error"
      field: "income" | "expenses" | "wages" | "hours" | "days" | "weeks"
      message: string
    }

const MAX_TEXT = MAX_AMOUNT.toLocaleString("en-AU")

/** Validation messages and the one-line answer, by language. Numbers in them come from the arguments. */
export interface SoleModelCopy {
  incomeEmpty: string
  incomeBad: (max: string) => string
  expensesBad: (max: string) => string
  wagesBad: (max: string) => string
  hoursBad: string
  daysBad: string
  weeksBad: string
  outOfRange: string
  answer: (p: {
    income: string
    expenses: string
    perMonth: string
    fy: string
    total: string
    share: string
    takeHome: string
    medicare: boolean
  }) => string
}

const SOLE_MODEL_COPY_EN: SoleModelCopy = {
  incomeEmpty: "Enter your business income to see how much to set aside.",
  incomeBad: (max) => `Use an amount between 0 and ${max}, such as 100,000 or 85.50.`,
  expensesBad: (max) =>
    `Use an amount between 0 and ${max} for your business expenses, or leave it at 0.`,
  wagesBad: (max) => `Use an amount between 0 and ${max} for your wages, or leave it at 0.`,
  hoursBad: "Use hours a week from 1 to 100, such as 38.",
  daysBad: "Use days a week from 1 to 7, such as 5.",
  weeksBad: "Use whole weeks worked in the year, from 1 to 52.",
  outOfRange: "That income is out of range. Try a smaller amount.",
  answer: (p) =>
    `On ${p.income} of ABN income and ${p.expenses} of expenses, set aside about ${p.perMonth} a month for tax in ${p.fy} (${p.total} a year, ${p.share}% of what you earn), and keep about ${p.takeHome}. The estimate covers ${p.medicare ? "income tax and the Medicare levy" : "income tax"} and applies in every state.`,
}

const MODEL_COPY: Record<Locale, SoleModelCopy> = { en: SOLE_MODEL_COPY_EN, zh: SOLE_MODEL_COPY_ZH }

function annualise(form: SoleForm, amount: Money, hours: number, days: number, weeks: number) {
  switch (form.period) {
    case "year":
      return amount
    case "month":
      return multiplyByRatio(amount, 12, 1)
    case "week":
      return multiplyByRatio(amount, weeks, 1)
    case "day":
      return multiplyByRatio(amount, days * weeks, 1)
    case "hour":
      return multiplyByRatio(amount, Math.round(hours * 10) * weeks, 10)
  }
}

/** Validates the form at the boundary and runs the engine; all money is integer cents. */
export function evaluateSole(form: SoleForm, locale: Locale = "en"): SoleOutcome {
  const t = MODEL_COPY[locale]
  const zero = money(0, "AUD")
  if (form.income.trim() === "")
    return {
      kind: "error",
      field: "income",
      message: t.incomeEmpty,
    }
  const income = parseAmount(form.income)
  if (!income)
    return {
      kind: "error",
      field: "income",
      message: t.incomeBad(MAX_TEXT),
    }
  const expenses = form.expenses.trim() === "" ? zero : parseAmount(form.expenses)
  if (!expenses)
    return {
      kind: "error",
      field: "expenses",
      message: t.expensesBad(MAX_TEXT),
    }
  const wages = form.wages.trim() === "" ? zero : parseAmount(form.wages)
  if (!wages)
    return {
      kind: "error",
      field: "wages",
      message: t.wagesBad(MAX_TEXT),
    }
  const hours = parseHours(form.hoursPerWeek)
  if (form.period === "hour" && hours === undefined)
    return { kind: "error", field: "hours", message: t.hoursBad }
  const days = parseDays(form.daysPerWeek)
  if (form.period === "day" && days === undefined)
    return { kind: "error", field: "days", message: t.daysBad }
  const weeks = parseWeeks(form.weeksPerYear)
  if (["week", "day", "hour"].includes(form.period) && weeks === undefined)
    return {
      kind: "error",
      field: "weeks",
      message: t.weeksBad,
    }

  const annualIncome = annualise(form, income, hours ?? 38, days ?? 5, weeks ?? 52)
  // An hourly or daily rate can annualise far above the typed amount.
  if (annualIncome.amount > MAX_AMOUNT * 100)
    return {
      kind: "error",
      field: "income",
      message: t.outOfRange,
    }
  const base = {
    financialYear: form.fy,
    residency: form.residency,
    hasStudyLoan: form.help,
    applyMedicareLevy: form.medicare,
  }
  const result = soleTraderTax({
    ...base,
    businessIncome: annualIncome,
    businessExpenses: expenses,
    wages,
  })
  const employee = takeHomePay({
    ...base,
    amount: annualIncome,
    period: "year",
    hoursPerWeek: 38,
    daysPerWeek: 5,
    includesSuper: false,
    otherAnnualIncome: wages,
    applyStandardDeduction: false,
  })
  return { kind: "ok", result, annualIncome, expenses, wages, employee }
}

export type SoleShareKey = "takeHome" | "expenses" | "tax" | "medicare" | "help"
export interface SoleSharePart {
  key: SoleShareKey
  amount: Money
  /** Share of business income in basis points; the five shares sum to exactly 10,000 (largest remainder). */
  shareBp: number
}

/** Where business income goes. Expenses above the income are shown as the whole income. Display only. */
export function soleShares(
  incomeCents: number,
  expensesCents: number,
  r: SoleTraderResult,
): SoleSharePart[] {
  const parts: [SoleShareKey, number][] = [
    ["takeHome", r.takeHome.amount],
    ["expenses", Math.min(expensesCents, incomeCents)],
    ["tax", r.business.incomeTaxAfterLito.amount],
    ["medicare", r.business.medicareLevy.amount],
    ["help", r.business.studyLoanRepayment.amount],
  ]
  if (incomeCents <= 0)
    return parts.map(([key, v]) => ({ key, amount: money(v, "AUD"), shareBp: 0 }))
  const raw = parts.map(([, v]) => Math.max(0, v) * 10_000)
  const floors = raw.map((v) => Math.floor(v / incomeCents))
  let left = 10_000 - floors.reduce((a, b) => a + b, 0)
  const order = raw
    .map((v, k) => ({ k, rest: v % incomeCents }))
    .toSorted((a, b) => b.rest - a.rest || a.k - b.k)
  for (const { k } of order) {
    if (left <= 0) break
    floors[k] = (floors[k] ?? 0) + 1
    left -= 1
  }
  return parts.map(([key, v], k) => ({ key, amount: money(v, "AUD"), shareBp: floors[k] ?? 0 }))
}

/** The prerendered one-line answer; the page copy and the test both build it from the engine. */
export function answerSentence(
  form: SoleForm,
  formatAud: (value: Money) => string,
  locale: Locale = "en",
): string {
  const outcome = evaluateSole(form, locale)
  if (outcome.kind !== "ok") throw new Error("answerSentence needs a valid form")
  const { result, annualIncome, expenses } = outcome
  const whole = (v: Money) => formatAud(v).replace(/\.00$/, "")
  const share = new Intl.NumberFormat("en-AU", { maximumFractionDigits: 1 }).format(
    result.setAside.shareOfIncomeBp / 100,
  )
  return MODEL_COPY[locale].answer({
    income: whole(annualIncome),
    expenses: whole(expenses),
    perMonth: formatAud(result.setAside.perMonth),
    fy: form.fy.replace("-", "–"),
    total: whole(result.business.total),
    share,
    takeHome: whole(result.takeHome),
    medicare: form.medicare,
  })
}
